import moment from 'moment'
import { OrganizationSunsetInterface } from '@/types/interfaces'

export const SUNSET_WARNING_WINDOW_DAYS = 30

export function getSunsetEffectiveAt(
  sunset: OrganizationSunsetInterface | undefined
): Date | null {
  if (!sunset?.effectiveAt) return null
  const effectiveAt = sunset.effectiveAt instanceof Date
    ? sunset.effectiveAt
    : new Date(sunset.effectiveAt)
  if (isNaN(effectiveAt.getTime())) return null
  return effectiveAt
}

/** Whole calendar days from start of today until start of effectiveAt (0 = today). */
export function calendarDaysUntilSunset(
  sunset: OrganizationSunsetInterface | undefined,
  now: Date = new Date()
): number | null {
  const effectiveAt = getSunsetEffectiveAt(sunset)
  if (!effectiveAt) return null
  return moment(effectiveAt).startOf('day').diff(moment(now).startOf('day'), 'days')
}

export function isOrgSunsetWarningVisible(
  sunset: OrganizationSunsetInterface | undefined,
  windowDays = SUNSET_WARNING_WINDOW_DAYS,
  now: Date = new Date()
): boolean {
  if (!getSunsetEffectiveAt(sunset)) return false
  if (isOrgSunsetActive(sunset, now)) return false
  const days = calendarDaysUntilSunset(sunset, now)
  if (days === null) return false
  return days >= 0 && days <= windowDays
}

export function formatSunsetWarningDate(
  sunset: OrganizationSunsetInterface | undefined
): string {
  const effectiveAt = getSunsetEffectiveAt(sunset)
  if (!effectiveAt) return '—'
  return moment(effectiveAt).format('DD.MM.YYYY')
}

export function isOrgSunsetActive(
  sunset: OrganizationSunsetInterface | undefined,
  now: Date = new Date()
): boolean {
  const effectiveAt = getSunsetEffectiveAt(sunset)
  if (!effectiveAt) return false
  return now.getTime() >= effectiveAt.getTime()
}

export type SunsetBannerField = 'headline' | 'details'

/** Banner copy from Firestore when set, otherwise fallback (e.g. i18n default). */
export function resolveSunsetBannerText(
  sunset: OrganizationSunsetInterface | undefined,
  field: SunsetBannerField,
  localeIsDe: boolean,
  fallback: string,
  vars: { date: string; orgName: string }
): string {
  const de =
    field === 'headline' ? sunset?.bannerHeadlineDe : sunset?.bannerDetailsDe
  const en =
    field === 'headline' ? sunset?.bannerHeadlineEn : sunset?.bannerDetailsEn
  const primary = (localeIsDe ? de : en)?.trim()
  const secondary = (localeIsDe ? en : de)?.trim()
  const template = primary || secondary || fallback
  return formatSunsetMessage(template, vars)
}

/** Replaces {date}, {orgName} (and {organization}) in admin-authored sunset copy. */
export function formatSunsetMessage(
  text: string,
  vars: { date: string; orgName: string; days?: string | number }
): string {
  if (!text) return ''
  const days = vars.days !== undefined ? String(vars.days) : ''
  return text
    .replace(/\{date\}/gi, vars.date)
    .replace(/\{days\}/gi, days)
    .replace(/\{orgName\}/gi, vars.orgName)
    .replace(/\{organization\}/gi, vars.orgName)
}

/** Reference copy for platform admins (Super Admin / Firestore). Use {date} in message fields. */
export const SUNSET_COPY_REFERENCE = {
  de: {
    bannerHeadline: 'Ligurio wird zum {date} eingestellt.',
    bannerDetails:
      'Das Ligurio-Abonnement für diese Organisation endet planmäßig am {date}, da der Betrieb der Plattform Ligurio eingestellt wird. Bitte kontaktieren Sie Ihren Administrator für Fragen zum weiteren Betrieb Ihres Pressespiegels.',
    title: 'Ihr Zugang zu Ligurio wurde geschlossen',
    message: `Das Abonnement für diese Ligurio-Organisation ist zum {date} ausgelaufen.

Ein Zugriff auf die Inhalte und den Pressespiegel ist seit diesem Zeitpunkt nicht mehr möglich.

Bei Rückfragen oder Anliegen zur weiteren Vorgehensweise, oder falls Sie eine Reaktivierung wünschen, wenden Sie sich bitte an:`,
    contactTitle: 'Ansprechpartner bei Rückfragen',
  },
  en: {
    bannerHeadline: 'Ligurio will be discontinued on {date}.',
    bannerDetails:
      'The Ligurio subscription for this organization will end as planned on {date}, as operation of the Ligurio platform is being discontinued. Please contact your administrator for questions about the continued operation of your press digest.',
    title: 'Your access to Ligurio has been closed',
    message: `The subscription for this Ligurio organization ended on {date}.

Content and the press digest are no longer accessible since that date.

For questions, next steps, or if you would like to reactivate access, please contact:`,
    contactTitle: 'Contact for questions',
  },
}

export function sunsetMessageParagraphs(
  raw: string,
  vars: { date: string; orgName: string }
): string[] {
  const formatted = formatSunsetMessage(raw, vars)
  return formatted
    .split(/\n\s*\n/)
    .map(part => part.trim())
    .filter(Boolean)
}

export const emptyOrganizationSunset = (): OrganizationSunsetInterface => ({
  effectiveAt: null,
  titleDe: '',
  titleEn: '',
  messageDe: '',
  messageEn: '',
  bannerHeadlineDe: '',
  bannerHeadlineEn: '',
  bannerDetailsDe: '',
  bannerDetailsEn: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
})
